import { Router } from 'express';
import { Logger } from 'winston';
import cors from 'cors';
import { Config } from '@backstage/config';
import { DoraMetricsService } from './doraMetricsService';

export interface RouterOptions {
  logger: Logger;
  config: Config;
  service: DoraMetricsService;
}

export function createRouter(options: RouterOptions): Router {
  const { logger, config, service } = options;
  const router = Router();
  
  // Configure CORS to allow credentials and configurable origin
  const frontendUrl = config.getOptionalString('doraMetrics.frontendUrl') || 'http://localhost:8000';
  router.use(cors({
    origin: frontendUrl,
    credentials: true
  }));

  router.get('/health', (_, res) => {
    logger.info('DORA Metrics plugin health check');
    res.send({ status: 'ok' });
  });

  router.get('/projects', async (_, res) => {
    try {
      // Use the service method to get projects
      const projects = await service.getProjects();
      res.json(projects);
    } catch (error) {
      logger.error(`Error fetching projects: ${error}`);
      res.status(500).json({ error: 'Error fetching projects' });
    }
  });

  router.get('/metrics/deployment-frequency', async (req, res) => {
    const { projectKey, timeRange = '30d' } = req.query;

    if (!projectKey) {
      return res.status(400).json({ error: 'Project key is required' });
    }

    try {
      const data = await service.getDeploymentFrequency(
        projectKey as string,
        timeRange as string,
      );
      res.json(data);
    } catch (error) {
      logger.error(`Error fetching deployment frequency: ${error}`);
      res.status(500).json({ error: 'Error fetching deployment frequency' });
    }
  });

  router.get('/metrics/lead-time', async (req, res) => {
    const { projectKey, timeRange = '30d' } = req.query;

    if (!projectKey) {
      return res.status(400).json({ error: 'Project key is required' });
    }

    try {
      const data = await service.getLeadTimeForChanges(
        projectKey as string,
        timeRange as string,
      );
      res.json(data);
    } catch (error) {
      logger.error(`Error fetching lead time: ${error}`);
      res.status(500).json({ error: 'Error fetching lead time' });
    }
  });

  router.get('/metrics/mttr', async (req, res) => {
    const { projectKey, timeRange = '30d' } = req.query;

    if (!projectKey) {
      return res.status(400).json({ error: 'Project key is required' });
    }

    try {
      const data = await service.getMeanTimeToRestore(
        projectKey as string,
        timeRange as string,
      );
      res.json(data);
    } catch (error) {
      logger.error(`Error fetching MTTR: ${error}`);
      res.status(500).json({ error: 'Error fetching MTTR' });
    }
  });

  router.get('/metrics/failure-rate', async (req, res) => {
    const { projectKey, timeRange = '30d' } = req.query;

    if (!projectKey) {
      return res.status(400).json({ error: 'Project key is required' });
    }

    try {
      const data = await service.getChangeFailureRate(
        projectKey as string,
        timeRange as string,
      );
      res.json(data);
    } catch (error) {
      logger.error(`Error fetching failure rate: ${error}`);
      res.status(500).json({ error: 'Error fetching failure rate' });
    }
  });

  router.get('/metrics/summary', async (req, res) => {
    const { projectKey, timeRange = '30d' } = req.query;

    if (!projectKey) {
      return res.status(400).json({ error: 'Project key is required' });
    }

    try {
      const data = await service.getDoraMetricsSummary(
        projectKey as string,
        timeRange as string,
      );
      res.json(data);
    } catch (error) {
      logger.error(`Error fetching metrics summary: ${error}`);
      res.status(500).json({ error: 'Error fetching metrics summary' });
    }
  });

  // Authentication endpoints for Backstage frontend
  router.get('/auth/guest/refresh', (req, res) => {
    logger.info('Guest auth refresh request');
    res.json({
      profile: {
        email: 'guest@example.com',
        displayName: 'Guest User',
      },
      backstageIdentity: {
        token: 'guest-token',
        identity: {
          type: 'user',
          ownershipEntityRefs: ['user:default/guest'],
          userEntityRef: 'user:default/guest',
        },
      },
      expiresAt: new Date(Date.now() + 3600 * 1000).toISOString(),
    });
  });

  router.get('/auth/session', (req, res) => {
    logger.info('Auth session request');
    res.json({
      identity: {
        type: 'user',
        ownershipEntityRefs: ['user:default/guest'],
        userEntityRef: 'user:default/guest',
      },
      token: 'guest-token',
      expiresAt: new Date(Date.now() + 3600 * 1000).toISOString(),
    });
  });

  return router;
}
